import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSyncExternalStore } from 'react';
import { useNavigatableList } from '../../hooks/useNavigatableList';
import { NavigatableListStateContext, NavigatableListUpdateContext, NavigatableRegistrationContext, focusedIndexToString } from './context';
import NavigatableItemComponent from './NavigatableItem';
function createAutoDetectionStore() {
    let registeredItems = new Map();
    let clickHandlers = new Map();
    let listeners = new Set();
    let cachedSnapshot = { items: [], indexMap: new Map() };
    let currentVersion = 0;
    const rebuild = () => {
        const sorted = Array.from(registeredItems.entries())
            .filter(([, item]) => item.version === currentVersion)
            .sort((a, b) => a[0] - b[0]);
        const items = sorted.map(([, item]) => ({ childCount: item.children.size }));
        const indexMap = new Map(sorted.map(([originalIndex], idx) => [originalIndex, idx]));
        cachedSnapshot = { items, indexMap };
    };
    return {
        register(index) {
            if (index < 0)
                return;
            const existing = registeredItems.get(index);
            if (existing) {
                if (existing.version === currentVersion)
                    return;
                existing.version = currentVersion;
                existing.children = new Set();
            }
            else {
                registeredItems.set(index, { children: new Set(), version: currentVersion });
            }
            rebuild();
            listeners.forEach(l => l());
        },
        unregister() {
            // No-op: keeps navigation stable during virtual scroll.
            // Phantom items from old search views are handled by version filtering.
        },
        incrementVersion() {
            currentVersion++;
            rebuild();
            listeners.forEach(l => l());
        },
        registerChild(parentIndex, childIndex) {
            if (parentIndex < 0)
                return;
            let parent = registeredItems.get(parentIndex);
            if (!parent) {
                parent = { children: new Set(), version: currentVersion };
                registeredItems.set(parentIndex, parent);
            }
            if (!parent.children.has(childIndex)) {
                parent.children.add(childIndex);
                rebuild();
                listeners.forEach(l => l());
            }
        },
        unregisterChild(parentIndex, childIndex) {
            if (parentIndex < 0)
                return;
            const parent = registeredItems.get(parentIndex);
            if (parent && parent.children.has(childIndex)) {
                parent.children.delete(childIndex);
                rebuild();
                listeners.forEach(l => l());
            }
        },
        getNavigableIndex(index) {
            return cachedSnapshot.indexMap.get(index) ?? -1;
        },
        registerClickHandler(index, handler) {
            clickHandlers.set(focusedIndexToString(index), handler);
        },
        unregisterClickHandler(index) {
            clickHandlers.delete(focusedIndexToString(index));
        },
        triggerClickByKey(key) {
            const handler = clickHandlers.get(key);
            if (handler)
                handler();
        },
        getSnapshot() {
            return cachedSnapshot;
        },
        subscribe(listener) {
            listeners.add(listener);
            return () => listeners.delete(listener);
        }
    };
}
function NavigatableListRoot({ children, enabled = true, onReset, keyboardNavigatingClass = 'keyboard-navigating', navigateToFirstChild }) {
    const storeRef = useRef(null);
    if (!storeRef.current) {
        storeRef.current = createAutoDetectionStore();
    }
    const store = storeRef.current;
    // Incremented when search changes — causes visible NavigatableItems to re-register
    // with the new version, filtering out phantom items from the previous view.
    const [registrationVersion, setRegistrationVersion] = useState(0);
    const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
    const { focusedIndex, handleHover, handleFocus, isKeyboardNavigating } = useNavigatableList({
        navigableItems: snapshot.items,
        enabled,
        onReset,
        keyboardNavigatingClass,
        onEnter: store.triggerClickByKey,
        navigateToFirstChild
    });
    const isFirstRenderRef = useRef(true);
    useEffect(() => {
        if (isFirstRenderRef.current) {
            isFirstRenderRef.current = false;
            return;
        }
        store.incrementVersion();
        setRegistrationVersion(v => v + 1);
    }, [onReset]);
    const stateValue = useMemo(() => ({
        focusedIndex,
        isKeyboardNavigating
    }), [focusedIndex, isKeyboardNavigating]);
    const updateValue = useMemo(() => ({
        handleHover,
        handleFocus
    }), [handleHover, handleFocus]);
    // registrationVersion in deps causes a new context object on each version bump,
    // which triggers re-registration effects in all visible NavigatableItems.
    const registrationValue = useMemo(() => ({
        register: store.register,
        unregister: store.unregister,
        registerChild: store.registerChild,
        unregisterChild: store.unregisterChild,
        getNavigableIndex: store.getNavigableIndex,
        registerClickHandler: store.registerClickHandler,
        unregisterClickHandler: store.unregisterClickHandler
    }), [store, registrationVersion]); // eslint-disable-line react-hooks/exhaustive-deps
    return (_jsx(NavigatableRegistrationContext.Provider, { value: registrationValue, children: _jsx(NavigatableListStateContext.Provider, { value: stateValue, children: _jsx(NavigatableListUpdateContext.Provider, { value: updateValue, children: children }) }) }));
}
// Compound component pattern - attach sub-components as static properties
const NavigatableList = Object.assign(NavigatableListRoot, {
    Item: NavigatableItemComponent
});
export default NavigatableList;
